import { Box, MenuItem, Popover } from '@mui/material';
import { Typography, colors, IconButton } from '@sweep-io/sweep-design';
import { MoreActionsHorizontal } from '@sweep-io/sweep-design/dist/icons/MoreActionsHorizontal';
import { AvatarPicture } from '../../avatar/AvatarPicture';
import { DIMENSION_AVATAR_VERY_SMALL } from '../../../constants';
import UserSummaryBasic from './UserSummaryBasic';
import type { UserInfo } from '../../../types/UserInfoTypes';
import { RolesDropdown } from './RolesDropdown';
import { getIsUserInactive } from '../../../lib/getIsUserActive';
import type { UserDefinedRoleGroupsInterface } from '@server/role-group-interface';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { useState } from 'react';
import { usePermissions } from '../../../permissions/usePermissions';

export interface UserSummaryNewProps {
  user?: UserInfo;
  roles: UserDefinedRoleGroupsInterface[];
  onChangeRoleCb: (userId: string, newRole: string) => void;
  disableRoleDropdown?: boolean;
  onUserRemove?: (userId: string) => void;
  showBorders?: boolean;
}

const UserSummaryNew = ({
  user = {},
  roles,
  onChangeRoleCb,
  onUserRemove,
  disableRoleDropdown,
  showBorders,
}: UserSummaryNewProps) => {
  const { multiAccounts } = useFeatureToggle();
  const { name, email, roleGroupId: role, id, status } = user || {};
  const _userRole = roles.find((_role) => _role.id === role);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isAllowedDelete] = usePermissions().evaluatePermissions([
    { permission: ['delete:users'] },
  ]);

  const showActionsMenu = isAllowedDelete && !!onUserRemove && !!id;

  return (
    <UserSummaryBasic
      className="user-summary"
      data-testid="user-summary"
      key={id}
      sx={{
        borderBottom: multiAccounts ? `1px solid ${colors.grey[120]}` : undefined,
        alignItems: multiAccounts ? 'center' : undefined,
        '&:hover, &:active': {
          backgroundColor: multiAccounts ? colors.grey[100] : undefined,
        },
        backgroundColor: multiAccounts && isDropdownOpen ? colors.grey[100] : undefined,
        pr: multiAccounts ? '16px' : undefined,
        pl: multiAccounts ? '16px' : undefined,
      }}
    >
      <div className="user-summary-avatar">
        <AvatarPicture
          avatar={{ emoji: user.emoji, imageUrl: user.imageUrl }}
          dimension={DIMENSION_AVATAR_VERY_SMALL}
          isInactive={getIsUserInactive(status)}
        />
      </div>
      <Box
        display="flex"
        flexDirection="column"
        overflow="hidden"
        className="text-info"
        flex="1 0"
        minHeight={multiAccounts ? '80px' : undefined}
        justifyContent={multiAccounts ? 'center' : undefined}
      >
        <Typography
          data-testid="user-summary-name"
          ellipsis
          variant={multiAccounts ? 'body-medium' : 'body'}
        >
          {name}
        </Typography>
        <Typography data-testid="user-summary-email" ellipsis variant="body" color="text.secondary">
          {email}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {user && (
          <RolesDropdown
            onChangeRoleCb={onChangeRoleCb}
            accountRoles={roles}
            disableRoleDropdown={disableRoleDropdown}
            userRole={_userRole}
            userId={id}
            onOpen={() => setIsDropdownOpen(true)}
            onClose={() => setIsDropdownOpen(false)}
            showBorders={showBorders}
            withStaticWidth
          />
        )}

        {showActionsMenu && (
          <>
            <IconButton
              size="tiny"
              variant="flat"
              onClick={(e) => setActionsAnchorEl(e.currentTarget)}
            >
              <MoreActionsHorizontal size="medium" />
            </IconButton>
            <Popover
              open={Boolean(actionsAnchorEl)}
              anchorEl={actionsAnchorEl}
              onClose={() => setActionsAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                sx={{ color: colors.blush[500], p: 1, minWidth: '120px' }}
                onClick={() => {
                  onUserRemove(id);
                  setActionsAnchorEl(null);
                }}
              >
                Delete
              </MenuItem>
            </Popover>
          </>
        )}
      </Box>
    </UserSummaryBasic>
  );
};

export { UserSummaryNew };
